/**
 * Supabase-backed store. Requires VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env (see .env.example).
 * Progress is saved as rows (unit_progress, fact_progress, warmup_days) so the teacher view can query it.
 */
import { createClient, type SupabaseClient, type Session } from "@supabase/supabase-js";
import { emptyProgress, type StudentProgress, type FactStatus } from "../engine/progress";
import { parseKey, keyOp, factKey } from "../engine/curriculum";
import type { Store, Student, AttemptLog, AttemptRow, SkillAttemptLog, SkillAttemptRow, ClassInfo, ClassMember, Membership, ActivityRow, RosterRow, Access, Plan, License, LicenseKind, CheckoutPlan } from "./store";

/** True when an error says a table or column from a migration not yet run is missing. */
const missing = (e: { message?: string; code?: string } | null | undefined): boolean =>
  !!e && (e.code === "42P01" || e.code === "42703" || e.code === "PGRST204" || e.code === "PGRST205" || /does not exist|Could not find/i.test(e.message ?? ""));
let warned = false;
const warnOnce = (what: string) => {
  if (!warned) console.warn(`Supabase: ${what} — run supabase/migrations/0002_lessons.sql. Saved in this browser meanwhile.`);
  warned = true;
};
const LS_LESSONS = "tigardLessons";
const localLessons = (): Record<string, unknown> => { try { return JSON.parse(localStorage.getItem(LS_LESSONS) ?? "{}"); } catch { return {}; } };
const saveLocalLessons = (o: Record<string, unknown>) => { try { localStorage.setItem(LS_LESSONS, JSON.stringify(o)); } catch { /* ignore */ } };

export const supabaseConfigured = (): boolean => !!(import.meta.env.VITE_SUPABASE_URL && import.meta.env.VITE_SUPABASE_ANON_KEY);

let client: SupabaseClient | null = null;
let closing = false;
export function supabase(): SupabaseClient {
  if (!client) client = createClient(import.meta.env.VITE_SUPABASE_URL as string, import.meta.env.VITE_SUPABASE_ANON_KEY as string, {
    // While the page is closing, small writes go out with keepalive so the browser finishes them.
    global: { fetch: (input, init) => fetch(input, closing && init && typeof init.body === "string" && init.body.length < 60000 ? { ...init, keepalive: true } : init) },
  });
  return client;
}

export async function session(): Promise<Session | null> {
  const { data } = await supabase().auth.getSession();
  return data.session;
}

/** Email + password sign-in/up for the adult. (Magic-link is the alternative; this is simplest for the pilot.) */
export const auth = {
  signIn: (email: string, password: string) => supabase().auth.signInWithPassword({ email, password }),
  signUp: (email: string, password: string, displayName: string) =>
    supabase().auth.signUp({ email, password, options: { data: { display_name: displayName } } }),
  signOut: () => supabase().auth.signOut(),
  onChange: (fn: (s: Session | null) => void) => supabase().auth.onAuthStateChange((_e, s) => fn(s)),
};

export class SupabaseStore implements Store {
  readonly kind = "supabase" as const;
  constructor(private readonly ownerId: string) {}

  async listStudents(): Promise<Student[]> {
    // Only this adult's own children: a teacher's class members are read in the teacher view, never in the picker.
    const { data, error } = await supabase().from("students").select("id, first_name, grade").eq("owner_id", this.ownerId).order("created_at");
    if (error) throw error;
    return (data ?? []).map((r) => ({ id: r.id, firstName: r.first_name, grade: r.grade }));
  }
  async addStudent(firstName: string, grade: number | null): Promise<Student> {
    const { data, error } = await supabase().from("students").insert({ owner_id: this.ownerId, first_name: firstName, grade }).select("id, first_name, grade").single();
    if (error) throw error;
    return { id: data.id, firstName: data.first_name, grade: data.grade };
  }
  async removeStudent(id: string): Promise<void> {
    const { error } = await supabase().from("students").delete().eq("id", id);
    if (error) throw error;
  }
  /* ---------- Progress: load once, then save only what changed ---------- */
  // At scale (Nov 2, 2026): a save used to re-send every fact row, unit and warm-up day on every answer. Now the
  // store remembers what the database already has and sends only the rows that changed; saves made in quick
  // succession are combined (at most one write every SAVE_MS per student), and anything waiting is written at once
  // when the page is hidden or the student is reopened.
  private saved = new Map<string, Snapshot>();
  private pending = new Map<string, { p: StudentProgress; timer: ReturnType<typeof setTimeout> | null; waiters: { ok: () => void; no: (e: unknown) => void }[]; tries?: number }>();
  private inflight = new Map<string, Promise<void>>();
  private hooked = false;

  async loadProgress(studentId: string): Promise<StudentProgress> {
    await this.flush(studentId, true); // writes everything waiting (retries included) and waits for every write in flight
    return (await this.loadProgressMany([studentId])).get(studentId) ?? emptyProgress();
  }
  /** Many students in four queries (the teacher roster), not four queries per student. */
  async loadProgressMany(ids: string[]): Promise<Map<string, StudentProgress>> {
    const out = new Map<string, StudentProgress>(ids.map((id) => [id, emptyProgress()]));
    for (let i = 0; i < ids.length; i += 100) {
      const chunk = ids.slice(i, i + 100);
      const [st, up, fp, wd] = await Promise.all([
        supabase().from("students").select("id, current_unit, lesson_state").in("id", chunk),
        supabase().from("unit_progress").select("student_id, unit_id, stage, warm_date").in("student_id", chunk).limit(100 * chunk.length),
        allRows((from, to) => supabase().from("fact_progress").select("student_id, a, b, op, status, best_ms").in("student_id", chunk).order("student_id").range(from, to)),
        allRows((from, to) => supabase().from("warmup_days").select("student_id, day").in("student_id", chunk).order("student_id").range(from, to)),
      ]);
      if (st.error) throw st.error;
      for (const r of st.data ?? []) {
        const p = out.get(r.id)!;
        if (r.current_unit) p.currentUnit = r.current_unit;
        const state = (r.lesson_state && Object.keys(r.lesson_state).length ? r.lesson_state : localLessons()[r.id]) as Partial<StudentProgress> | undefined;
        if (state) {
          p.lessons = state.lessons ?? {};
          p.skills = state.skills ?? {};
          p.checks = state.checks ?? {};
          if (state.floor) p.floor = state.floor;
          if (state.placement) p.placement = state.placement;
          if (state.diagnostic) p.diagnostic = state.diagnostic;
          if (state.factsTest) p.factsTest = state.factsTest;
        }
      }
      for (const r of up.data ?? []) out.get(r.student_id)!.units[r.unit_id] = { stage: r.stage, warm: r.warm_date ?? "" };
      for (const r of fp) out.get(r.student_id)!.facts[factKey(r.a, r.b, r.op === "div" ? "div" : "mul")] = { s: r.status as FactStatus, ...(r.best_ms != null ? { ms: r.best_ms } : {}) };
      for (const r of wd) out.get(r.student_id)!.warmDays.push(r.day);
    }
    for (const [id, p] of out) this.saved.set(id, snapshot(p));
    return out;
  }
  saveProgress(studentId: string, p: StudentProgress): Promise<void> {
    if (!this.hooked && typeof document !== "undefined") {
      this.hooked = true;
      document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") { closing = true; void this.flushAll().finally(() => { closing = false; }); } });
      window.addEventListener("pagehide", () => { closing = true; void this.flushAll(); });
    }
    return new Promise((ok, no) => {
      const q = this.pending.get(studentId) ?? { p, timer: null, waiters: [] };
      q.p = p;
      q.waiters.push({ ok, no });
      if (!q.timer) q.timer = setTimeout(() => void this.flush(studentId), SAVE_MS);
      this.pending.set(studentId, q);
    });
  }
  private async flushAll(): Promise<void> { await Promise.all([...this.pending.keys()].map((id) => this.flush(id))); }
  /** Write whatever is waiting for this student now, and return only when no write for them is in flight. */
  async flush(studentId: string, force = false): Promise<void> {
    for (;;) {
      const busy = this.inflight.get(studentId);
      if (busy) { await busy.catch(() => undefined); continue; }
      const q = this.pending.get(studentId);
      if (!q) return;
      this.pending.delete(studentId);
      if (q.timer) clearTimeout(q.timer);
      const run = this.write(studentId, q.p);
      this.inflight.set(studentId, run);
      try { await run; q.waiters.forEach((w) => w.ok()); }
      catch (e) {
        q.waiters.forEach((w) => w.no(e));
        // Offline for a moment? Try the same state again shortly, unless something newer is already waiting.
        if (!this.pending.has(studentId) && (q.tries ?? 0) < 5) {
          this.pending.set(studentId, { p: q.p, waiters: [], tries: (q.tries ?? 0) + 1, timer: setTimeout(() => void this.flush(studentId), 5000 * ((q.tries ?? 0) + 1)) });
        }
      }
      finally { if (this.inflight.get(studentId) === run) this.inflight.delete(studentId); }
      if (!force && this.pending.get(studentId)?.timer && this.pending.get(studentId)!.waiters.length === 0) return; // a retry is scheduled
    }
  }
  private async write(studentId: string, p: StudentProgress): Promise<void> {
    const sb = supabase();
    const now = new Date().toISOString();
    const next = snapshot(p), was = this.saved.get(studentId) ?? emptySnapshot();
    const facts = [...next.facts].filter(([k, v]) => was.facts.get(k) !== v).map(([k]) => {
      const [a, b] = parseKey(k), f = p.facts[k]!;
      return { student_id: studentId, a, b, op: keyOp(k), status: f.s, best_ms: f.ms ?? null, updated_at: now };
    });
    const units = [...next.units].filter(([k, v]) => was.units.get(k) !== v).map(([unit_id]) => ({ student_id: studentId, unit_id, stage: p.units[unit_id]!.stage, warm_date: p.units[unit_id]!.warm || null, updated_at: now }));
    const days = [...next.days].filter((d) => !was.days.has(d)).map((day) => ({ student_id: studentId, day }));
    const row: Record<string, unknown> = {};
    if (next.unit !== was.unit) row.current_unit = p.currentUnit;
    if (next.state !== was.state) row.lesson_state = JSON.parse(next.state);
    const jobs: PromiseLike<{ error: { message?: string; code?: string } | null }>[] = [];
    if (Object.keys(row).length) jobs.push(sb.from("students").update(row).eq("id", studentId));
    if (units.length) jobs.push(sb.from("unit_progress").upsert(units));
    if (days.length) jobs.push(sb.from("warmup_days").upsert(days, { ignoreDuplicates: true }));
    if (facts.length) jobs.push(sb.from("fact_progress").upsert(facts, { onConflict: "student_id,a,b,op" }));
    const results = await Promise.all(jobs);
    const err = results.find((r) => r.error)?.error;
    if (err) throw err; // nothing is marked saved, so the next write sends it all again
    this.saved.set(studentId, next);
  }
  async logSkillAttempt(at: SkillAttemptLog): Promise<void> {
    const { error } = await supabase().from("skill_attempts").insert({
      student_id: at.studentId, lesson_id: at.lessonId, step_id: at.stepId, skill: at.skill, item_id: at.itemId,
      response: at.response.slice(0, 60), correct: at.correct, first_try: at.firstTry, hint_level: at.hintLevel,
      misconception: at.misconception, ms: Math.round(at.ms),
    });
    if (missing(error)) warnOnce("skill_attempts table missing");
  }
  async listSkillAttempts(studentId: string, limit = 60): Promise<SkillAttemptRow[]> {
    const { data, error } = await supabase().from("skill_attempts").select("lesson_id, step_id, skill, item_id, response, correct, first_try, hint_level, misconception, ms, at").eq("student_id", studentId).order("at", { ascending: false }).limit(limit);
    if (error) return [];
    return (data ?? []).map((r) => ({ studentId, lessonId: r.lesson_id, stepId: r.step_id, skill: r.skill, itemId: r.item_id, response: r.response, correct: r.correct, firstTry: r.first_try, hintLevel: r.hint_level, misconception: r.misconception, ms: r.ms ?? 0, at: r.at }));
  }
  async listAttempts(studentId: string, limit = 40): Promise<AttemptRow[]> {
    const { data, error } = await supabase().from("attempts").select("unit_id, stage, a, b, correct, first_try, used_tool, ms, at").eq("student_id", studentId).order("at", { ascending: false }).limit(limit);
    if (error) throw error;
    return (data ?? []).map((r) => ({ studentId, unitId: r.unit_id, stage: r.stage, a: r.a, b: r.b, correct: r.correct, firstTry: r.first_try, usedTool: r.used_tool, ms: r.ms ?? 0, at: r.at }));
  }
  async logAttempt(at: AttemptLog): Promise<void> {
    await supabase().from("attempts").insert({
      student_id: at.studentId, unit_id: at.unitId, stage: at.stage, a: at.a, b: at.b,
      correct: at.correct, first_try: at.firstTry, used_tool: at.usedTool, ms: Math.round(at.ms),
    });
  }

  /* ---------- Rosters and paid access (migration 0004) ---------- */
  async classRoster(classId: string): Promise<RosterRow[] | null> {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    const { data, error } = await supabase().rpc("class_roster", { cid: classId, tz });
    if (error) { if (missing(error)) return null; throw error; }
    return ((data ?? []) as RosterJson[]).map((r) => {
      const p = emptyProgress(), st = (r.lesson_state ?? {}) as Partial<StudentProgress>;
      p.lessons = st.lessons ?? {}; p.skills = st.skills ?? {}; p.checks = st.checks ?? {};
      if (st.floor) p.floor = st.floor; if (st.placement) p.placement = st.placement; if (st.diagnostic) p.diagnostic = st.diagnostic; if (st.factsTest) p.factsTest = st.factsTest;
      return { member: { studentId: r.student_id, studyCode: r.study_code, joinedAt: r.joined_at, firstName: r.first_name, grade: r.grade }, progress: p,
        weeks: r.weeks ?? {}, answers: r.answers, firstTry: r.first_try, top: r.top ?? [], fluent: r.fluent, correct: r.correct };
    });
  }
  async access(studentId: string): Promise<Access> {
    const { data, error } = await supabase().rpc("student_access", { sid: studentId });
    // Before migration 0004 runs (or if the check can't be reached), nothing is held back.
    if (error) return { ok: true, via: "open" };
    return data as Access;
  }
  async myPlan(): Promise<Plan | null> {
    const { data, error } = await supabase().rpc("my_plan");
    if (error) return null;
    const d = data as { comp: boolean; role: Plan["role"]; license: Record<string, unknown> | null };
    return { comp: d.comp, role: d.role, license: d.license ? toLicense(d.license) : null };
  }
  async redeemTeacherCode(code: string) {
    const { data, error } = await supabase().rpc("redeem_teacher_code", { code });
    if (error) throw new Error(error.message.replace(/^.*?: /, ""));
    return data as { name: string; kind: string; seats: number };
  }
  private async api(path: string, body: unknown): Promise<string> {
    const s = await session();
    const r = await fetch(path, { method: "POST", headers: { "content-type": "application/json", authorization: `Bearer ${s?.access_token ?? ""}` }, body: JSON.stringify(body) });
    const j = (await r.json().catch(() => ({}))) as { url?: string; error?: string };
    if (!r.ok || !j.url) throw new Error(j.error ?? "Payments aren't set up yet.");
    return j.url;
  }
  checkout(plan: CheckoutPlan, students: number) { return this.api("/api/checkout", { plan, students }); }
  billingPortal() { return this.api("/api/portal", {}); }
  async adminLicenses(): Promise<License[]> {
    const { data, error } = await supabase().rpc("admin_licenses");
    if (error) return [];
    return ((data ?? []) as Record<string, unknown>[]).map(toLicense);
  }
  async adminCreateLicense(kind: LicenseKind, name: string, seats: number, endsAt: string | null, notes: string): Promise<License> {
    const { data, error } = await supabase().rpc("admin_create_license", { kind, name, seats, ends_at: endsAt, notes: notes || null });
    if (error) throw new Error(error.message);
    return toLicense({ ...(data as Record<string, unknown>), used: 0, teachers: 0 });
  }
  async adminUpdateLicense(id: string, seats: number | null, endsAt: string | null, status: License["status"] | null): Promise<void> {
    const { error } = await supabase().rpc("admin_update_license", { lid: id, seats, ends_at: endsAt, status });
    if (error) throw new Error(error.message);
  }

  /* ---------- Classes and the pilot study (migration 0003) ---------- */
  /** Until migration 0005 runs, the class-mode columns don't exist: fall back to the older column list. */
  private async classCols(): Promise<string> {
    if (this.hasClassMode === undefined) {
      const { error } = await supabase().from("classes").select("level, pace_lesson").limit(1);
      this.hasClassMode = !(error && missing(error));
    }
    return this.hasClassMode ? "id, name, join_code, code_prefix, weekly_minutes, level, pace_lesson" : "id, name, join_code, code_prefix, weekly_minutes";
  }
  private hasClassMode?: boolean;
  async listClasses(): Promise<ClassInfo[]> {
    const { data, error } = await supabase().from("classes").select(await this.classCols()).eq("owner_id", this.ownerId).order("created_at");
    if (error) { if (missing(error)) return []; throw error; }
    return ((data ?? []) as unknown as Parameters<typeof toClass>[0][]).map(toClass);
  }
  async createClass(name: string, prefix: string, weeklyMinutes: number | null): Promise<ClassInfo> {
    for (let i = 0; i < 5; i++) {
      const { data, error } = await supabase().from("classes").insert({ owner_id: this.ownerId, name, code_prefix: prefix, weekly_minutes: weeklyMinutes, join_code: newCode() }).select(await this.classCols()).single();
      if (!error) return toClass(data as unknown as Parameters<typeof toClass>[0]);
      if (error.code !== "23505") throw error; // try another code only on a clash
    }
    throw new Error("Could not make a unique class code. Try again.");
  }
  async setClassPace(classId: string, level: string | null, paceLesson: string | null): Promise<void> {
    const { error } = await supabase().from("classes").update({ level, pace_lesson: paceLesson }).eq("id", classId);
    if (error) throw new Error(missing(error) ? "Class mode isn't set up yet (migration 0005)." : error.message);
  }
  async setClassTarget(classId: string, weeklyMinutes: number | null): Promise<void> {
    const { error } = await supabase().from("classes").update({ weekly_minutes: weeklyMinutes }).eq("id", classId);
    if (error) throw error;
  }
  async listMembers(classId: string): Promise<ClassMember[]> {
    const { data, error } = await supabase().from("class_members").select("student_id, study_code, joined_at").eq("class_id", classId).order("study_code");
    if (error) { if (missing(error)) return []; throw error; }
    const ids = (data ?? []).map((r) => r.student_id);
    const st = ids.length ? await supabase().from("students").select("id, first_name, grade").in("id", ids) : { data: [] as { id: string; first_name: string; grade: number | null }[] };
    const byId = new Map((st.data ?? []).map((r) => [r.id, r]));
    return (data ?? []).map((r) => ({ studentId: r.student_id, studyCode: r.study_code, joinedAt: r.joined_at, firstName: byId.get(r.student_id)?.first_name ?? "", grade: byId.get(r.student_id)?.grade ?? null }));
  }
  async joinClass(studentId: string, code: string): Promise<Membership> {
    const { data, error } = await supabase().rpc("join_class", { code, sid: studentId });
    if (error) throw new Error(error.message.replace(/^.*?: /, ""));
    return { classId: data.class_id, className: data.name, studyCode: data.study_code, weeklyMinutes: data.weekly_minutes, level: data.level ?? null, paceLesson: data.pace_lesson ?? null };
  }
  async membership(studentId: string): Promise<Membership | null> {
    const { data, error } = await supabase().from("class_members").select("class_id, study_code, joined_at").eq("student_id", studentId).order("joined_at", { ascending: false }).limit(1);
    if (error || !data?.length) return null;
    const cols = (await this.classCols()).replace("id, name, join_code, code_prefix, ", "name, ");
    const c = await supabase().from("classes").select(cols).eq("id", data[0]!.class_id).single();
    const cd = (c.data ?? {}) as { name?: string; weekly_minutes?: number | null; level?: string | null; pace_lesson?: string | null };
    return { classId: data[0]!.class_id, className: cd.name ?? "", studyCode: data[0]!.study_code, weeklyMinutes: cd.weekly_minutes ?? null, level: cd.level ?? null, paceLesson: cd.pace_lesson ?? null };
  }
  async activity(studentId: string, sinceISO: string): Promise<ActivityRow[]> {
    const out: ActivityRow[] = [];
    const page = async (table: "skill_attempts" | "attempts") => {
      const cols = table === "skill_attempts" ? "at, ms, first_try, misconception" : "at, ms, first_try";
      for (let from = 0; ; from += 1000) {
        const { data, error } = await supabase().from(table).select(cols).eq("student_id", studentId).gte("at", sinceISO).order("at").range(from, from + 999);
        if (error) return;
        const rows = (data ?? []) as unknown as { at: string; ms: number | null; first_try: boolean; misconception?: string | null }[];
        for (const r of rows) out.push({ at: r.at, ms: r.ms ?? 0, firstTry: r.first_try, misconception: r.misconception ?? null });
        if (rows.length < 1000) return;
      }
    };
    await Promise.all([page("skill_attempts"), page("attempts")]);
    return out;
  }
}

const toClass = (r: { id: string; name: string; join_code: string | null; code_prefix: string | null; weekly_minutes: number | null; level?: string | null; pace_lesson?: string | null }): ClassInfo =>
  ({ id: r.id, name: r.name, joinCode: r.join_code ?? "", prefix: r.code_prefix ?? "", weeklyMinutes: r.weekly_minutes, level: r.level ?? null, paceLesson: r.pace_lesson ?? null });
/** Six letters and digits a parent can read off a page: no 0/O, 1/I/L. */
const newCode = (): string => { const A = "ABCDEFGHJKMNPQRSTUVWXYZ23456789"; let s = ""; const r = crypto.getRandomValues(new Uint32Array(6)); for (const x of r) s += A[x % A.length]; return s; };

/** How long saves for one student are gathered before one write (ms). */
export const SAVE_MS = 1500;
/** What the database holds for a student, as comparable strings. */
interface Snapshot { facts: Map<string, string>; units: Map<string, string>; days: Set<string>; unit: string; state: string }
const emptySnapshot = (): Snapshot => ({ facts: new Map(), units: new Map(), days: new Set(), unit: "", state: "" });
export function snapshot(p: StudentProgress): Snapshot {
  const state = { lessons: p.lessons ?? {}, skills: p.skills ?? {}, checks: p.checks ?? {}, ...(p.floor ? { floor: p.floor } : {}), ...(p.placement ? { placement: p.placement } : {}), ...(p.diagnostic ? { diagnostic: p.diagnostic } : {}), ...(p.factsTest ? { factsTest: p.factsTest } : {}) };
  return {
    facts: new Map(Object.entries(p.facts).map(([k, f]) => [k, `${f.s}|${f.ms ?? ""}`])),
    units: new Map(Object.entries(p.units).map(([k, u]) => [k, `${u.stage}|${u.warm ?? ""}`])),
    days: new Set(p.warmDays),
    unit: p.currentUnit,
    state: JSON.stringify(state),
  };
}
/** Every row of a query, 1,000 at a time (PostgREST's page size). */
async function allRows<T>(q: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: unknown }>): Promise<T[]> {
  const out: T[] = [];
  for (let from = 0; ; from += 1000) {
    const { data, error } = await q(from, from + 999);
    if (error) throw error;
    out.push(...(data ?? []));
    if ((data ?? []).length < 1000) return out;
  }
}

interface RosterJson { student_id: string; study_code: string; joined_at: string; first_name: string; grade: number | null; lesson_state: unknown; weeks: Record<string, number> | null; answers: number; first_try: number; top: { code: string; n: number }[] | null; fluent: number; correct: number }
const toLicense = (r: Record<string, unknown>): License => ({
  id: String(r.id), kind: r.kind as LicenseKind, name: (r.name as string) ?? null, seats: Number(r.seats ?? 0), used: Number(r.used ?? 0),
  status: (r.status as License["status"]) ?? "active", endsAt: (r.ends_at as string) ?? null, billing: (r.billing as License["billing"]) ?? "card",
  teacherCode: (r.teacher_code as string) ?? null, notes: (r.notes as string) ?? null, teachers: r.teachers != null ? Number(r.teachers) : undefined, buyer: r.buyer as boolean | undefined,
});
